import React, { useState, useEffect, useRef, useImperativeHandle } from 'react'
import moment from 'moment'
import { MapContainer, TileLayer, Pane, Polygon, Marker, Tooltip, Polyline, CircleMarker, Circle } from "react-leaflet"
import MarkerClusterGroup from 'react-leaflet-cluster'
import { featureCollection, clustersDbscan, point, convex, buffer, simplify } from '@turf/turf'
import { useMapEvents } from 'react-leaflet/hooks'
import { out_regions_shape, decoration } from './Settings'
import { hi_regions, lo_regions, shape_6r, zk, obj2regions, region_keys, firstRegion, reg_centers, markerTopTooltipOffset, markerBottomTooltipOffset, priority_colors, industry_icon, existed, fixHtml, code2region, oktmo2name } from './Utils'
import "leaflet/dist/leaflet.css"

const Map = React.forwardRef((props, ref) => {
	
	const { ENV, mapType, height, mapScale, data, filters, dialogs, userSettings, _specobjects } = props
	
	const [_map, _setMap] = useState(null)
	const [zoom, setZoom] = useState(mapType.ZOOM)
	const [center, setCenter] = useState(mapType.CENTER)
	
	/*
	useEffect(() => {
		document.documentElement.style.setProperty('--map-cursor', zoom > 13 ? 'context-menu' : 'grab')
	}, [zoom])
	*/

/*
 * MOVE TO FILTERS / UTILS
 */

// TEMP helper
const isRelativlyNotResolved = event => filters.isRelativlyNotResolved(event)

const eventCompare = (a, b) => {
	const pa = (a._e === 't' ? -2 : a._m === 't' ? -1 : Object.keys(priority_colors).indexOf(a.priority)) + (isRelativlyNotResolved(a) ? 0 : 10)
	const pb = (b._e === 't' ? -2 : b._m === 't' ? -1 : Object.keys(priority_colors).indexOf(b.priority)) + (isRelativlyNotResolved(b) ? 0 : 10)
	return pa - pb
}
const createEventCluster = (cluster) => {
	const clustered_events = cluster.getAllChildMarkers().map(e => e.options.uuid)
	const clusterWithDetails = existed(dialogs, clustered_events) !== undefined
	const major_id = cluster.getAllChildMarkers().sort((a, b) => a.options.ix - b.options.ix)[0].options.uuid
	const major = data.fEvents.find(e => e.id === major_id)
	const color = major._m === 't' || major._e === 't' ? 'red' : priority_colors[major.priority]
	const scale = 1 / mapScale
	const weight = 4 * scale * ((major._m === 't' || major._e === 't') && isRelativlyNotResolved(major) ? MASS_EXT : 1)
	const fontsize = (isRelativlyNotResolved(major) ? ACTUAL_EVENT_RADIUS : CLOSED_EVENT_RADIUS) / 18
	const unresolved = (isRelativlyNotResolved(major) ? '' : ' cluster-icon-resolved')
	const radius = (isRelativlyNotResolved(major) ? CLASTER_RADIUS : CLOSED_EVENT_RADIUS) * (major._m === 't' || major._e === 't' ? MASS_EXT : 1)
	return L.divIcon({ className: 'cluster-icon-container' + (clusterWithDetails ? ' cluster-with-details' : ''), iconSize: radius,
		html: `<div class="cluster-icon cluster-icon-${color}${unresolved}" style="border-width: ${weight}px;" alt="${clustered_events.join(' ')}"><div class="cluster-icon-label" style="font-size: ${fontsize}em">${cluster.getChildCount()}</div></div>`
	})
}
const createSpecobjectCluster = (cluster) => {
	//console.log(cluster.getAllChildMarkers())
	const clustered_objects = ['name1', 'name2']
	const radius = CLASTER_RADIUS
	const color = 'blue'
	const scale = 1 / mapScale
	const weight = 4 * scale
	const fontsize = ACTUAL_EVENT_RADIUS / 18
	return L.divIcon({ className: 'cluster-icon-container', iconSize: radius,
		html: `<div class="cluster-icon cluster-icon-${color}" style="border-width: ${weight}px;" alt="${clustered_objects.join(' ')}"><div class="cluster-icon-label" style="font-size: ${fontsize}em">${cluster.getChildCount()}</div></div>`
	})
}


const iconScale = (num) => num / (1 - (1 - mapScale) / 1.25 )

const CLASTER_RADIUS = iconScale(42) // 36
const clasterRadius = () => CLASTER_RADIUS - 5
const HI_METEO_RADIUS = iconScale(5)
const LO_METEO_RADIUS = iconScale(3)
const ACTUAL_EVENT_RADIUS = iconScale(36)
const CLOSED_EVENT_RADIUS = iconScale(30)
const MASS_EXT = 1.25
const DEENERGIZED_RADIUS = iconScale(24)
	
const eventMarker = (event, i) => {
	const objs = event.object_uuids.map(e => data.objCache[e]).filter(e => e && e.latlng).map(e => e.latlng)
	const cntr = objs.length ? objs.reduce((a, e) => a.map((el ,i) => a[i] += e[i]), [0, 0]).map(e => e / objs.length) : findCenter(firstRegion(event.regionId)) // [reg_centers.find(e => e.id == k).lat, reg_centers.find(e => e.id == k).lon]
	const isActive = dialogs.length && (dialogs[0].active === event.id)
	const color = '#c00'
	const radius = (isRelativlyNotResolved(event) ? ACTUAL_EVENT_RADIUS : CLOSED_EVENT_RADIUS) * (event._m === 't' || event._e === 't' ? MASS_EXT : 1)
	const opacity = isActive ? 0 : isRelativlyNotResolved(event) ? 1 : (event._m === 't' || event._e === 't') ? 0.8 : 0.6
	//const y = _map.latLngToLayerPoint(cntr).y
	//if (isActive) console.log(y)
	return <Marker key={i} icon={icon(eventIconName(event), radius)} position={cntr} pane={'events'} opacity={opacity} uuid={event.id} ix={i} alt={event.id}
						eventHandlers={{'click': (e) => {
							props.onShowEventDetails([e.target.options.uuid])
						}}} >
		{!isActive && <Tooltip className="marker-tooltip" sticky={true} offset={markerTopTooltipOffset} direction={'top'}>{eventTooltip(event)}</Tooltip>}
	</Marker>
}

const deenergizedMarker = (e, i) => {
	const reg = reg_centers.find(r => r.id === code2region[e.region_code].oktmo)
	const color = e.population < 500 ? 'y' : e.population < 5000 ? 'o' : 'r'
	return <Marker key={`${e.region_code}-${e.population}`} icon={icon(`user-${color}`, DEENERGIZED_RADIUS, +1, -1)} position={[reg.lat, reg.lon]} pane={'deenergized'} region={e.region_code} alt={props.deenergizedId + e.region_code}
		eventHandlers={{'click': (e) => {
			props.onShowDeenergized(e.target.options.region)
		}}} >
		<Tooltip className="marker-tooltip" sticky={true} offset={markerTopTooltipOffset} direction={'top'}>{deenergizedTooltip(e)}</Tooltip>
	</Marker>
}

const fireRegMarker = (k, v, i) => {
	const reg = reg_centers.find(r => r.id === code2region[k].oktmo)
	const color = v.length <= 10 ? 'y' : v.length < 50 ? 'o' : 'r'
	return <Marker key={`${k}-${v.length}`} icon={icon(`fire-${color}`, DEENERGIZED_RADIUS * (data.useCustomViewport ? data.customMarkerScale : 1), -1, -1)} 
		position={[reg.lat, reg.lon]} pane={'fireClusters'} region={k} alt={props.fireRegId + k}
		eventHandlers={{'click': (e) => {
			props.onShowFireReg(e.target.options.region)
		}}} >
		<Tooltip className="marker-tooltip" sticky={true} offset={markerTopTooltipOffset} direction={'top'}>{fireRegTooltip(k, v)}</Tooltip>
	</Marker>
}

const specobjectMarker = (o, i) => 
	<CircleMarker key={i} center={[o.lat, o.lng]} radius={10} pathOptions={specobjectOptions()} pane="events" name={o.name}>
		<Tooltip className="marker-tooltip" sticky={true} offset={markerTopTooltipOffset} direction={'top'}><p className="m-0">{o.name}</p></Tooltip>
	</CircleMarker>


const findCenter = (regId) => {
	const center = reg_centers.find(e => e.id == regId)
	return [center.lat, center.lon]
}

const icon = (img, size, dx = 0, dy = 0) => new L.Icon({
	iconUrl: ENV.imgUrl + img + '.svg',
	iconSize: [size, size],
	iconAnchor: [size/2*(1-(data.useCustomViewport ? 0 : dx)), size/2*(1+(data.useCustomViewport ? 0 : dy))],
})
const eventIconName = (event) => 
	event._e === 't'
	? (isRelativlyNotResolved(event) ? 'emergency' : 'emergency-resolved')
	: event._m === 't'
	? (isRelativlyNotResolved(event) ? 'mass' : 'mass-resolved')
	: event.is_nuclear === '1'
	? 'atom-' + priority_colors[event.priority]
	:	industry_icon[event.industry] + '-' + priority_colors[event.priority]
const eventTooltip = (event) => {
	return <>
		<p className="m-0 font-bold">{event.startincident}{event.de ? ' – ' + event.resolvedata : ''}</p>
		{event.regions.replace('{', '').replace('}', '').split(',').map(r => oktmo2name[r].replaceAll(' ', ' ')).map((e, i) => <p key={i} className="m-0">{e}</p>)}
		<div className="mb-2"></div>
		{event.title.map((e, i) => <p key={i} className="m-0">{e}</p>)}
	</>
}
const deenergizedTooltip = (e) => {
	return <>
		<p className="m-0 mb-2 font-bold">{e.region}</p>
		<p className="m-0">{e.company}</p>
		<p className="m-0"><span className="font-bold">{e.population}</span>{' абонентов обесточено'}</p>
	</>
}
const fireRegTooltip = (k, v) => {
	return <>
		<p className="m-0 mb-2 font-bold">{code2region[k].name}</p>
		<p className="m-0"><span className="font-bold">{v.length}</span>{' пожароопасных точек'}</p>
	</>
}

//console.log(data.svgFiles)

const earthquakeIcon = (key, quake, zoom) => {
	const latlng = [quake.inf.lat, quake.inf.lon]
	const int = Math.ceil(quake.inf.int)
	if (!_map) return
	const filename = ENV.imgUrl + `eq-${int}-${zoom}.svg`
	if (!data.svgFiles[filename]) {
		//console.log('generate', filename, int, zoom, [...Array(int).keys()].map(i => `<g id="${i}"></g>`).join(''))
		const _width = 32 / 2**zoom / int
		const _fop = 0.1 - (zoom - 4) / 30
		const _sop = 1 - (zoom - 4) / 10
		const content = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
		
<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		xmlns="http://www.w3.org/2000/svg"
		xmlns:svg="http://www.w3.org/2000/svg">
	<g>
		<circle
				style="fill:#000000;fill-opacity:0;stroke:#CC0000;stroke-width:${_width * 3};stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:0.1"
				cx="10"
				cy="10"
				r="10">
		</circle>
		<circle
				style="fill:#000000;fill-opacity:${_fop};stroke:#000000;stroke-width:${_width};stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:${_sop}"
				cx="10"
				cy="10"
				r="${_width * 2}">
		</circle>` + 
		[...Array(int).keys()].map(i => `
		<circle
				style="fill:#000000;fill-opacity:${_fop};stroke:#000000;stroke-width:${_width};stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:${_sop}"
				cx="10"
				cy="10"
				r="0">
			<animate attributeName="r" from="0" to="10" dur="${int}s" repeatCount="indefinite" begin="${i}s" />
			<animate attributeName="fill-opacity" from="${_fop}" to="0" dur="${int}s" repeatCount="indefinite" begin="${i}s" />
			<animate attributeName="stroke-opacity" from="${_sop}" to="0" dur="${int}s" repeatCount="indefinite" begin="${i}s" />
		</circle>
`).join('') + `
	</g>
</svg>`
		
		const blob = new Blob([content], { type: "image/svg+xml" })
		const file = URL.createObjectURL(blob)
		data.svgFiles[filename] = file
	}
	const _circle = L.circle(latlng, {radius: int*50000}).addTo(_map)
	const _northEast = _map.latLngToContainerPoint(_circle.getBounds().getNorthEast())
	const _southWest = _map.latLngToContainerPoint(_circle.getBounds().getSouthWest())
	_circle.removeFrom(_map)
	const radius = _northEast.x - _southWest.x
	const icon = new L.Icon({
		iconUrl: data.svgFiles[filename],
		iconSize: [radius, radius],
		iconAnchor: [radius/2, radius/2],
	})
	
	return <Marker key={key} icon={icon} position={latlng} pane={'quake'} opacity={1}>
		<Tooltip className="marker-tooltip earthquake" sticky={true} offset={markerTopTooltipOffset} direction={'top'}>{quakeTooltip(quake)}</Tooltip>
	</Marker>
}

const quakeTooltip = (quake) => <>
	<p className="m-0 mb-2 font-bold">{quake.ds.format('DD-MM-YYYY HH:mm')}</p>
	<p className="m-0">{quake.reg}</p>
	<p className="m-0" dangerouslySetInnerHTML={{ __html: fixHtml(quake.d) }}></p>
</>

//self.addEventListener('error', (e) => console.log(e))
/*
self.addEventListener("fetch", (event) => {
  event.respondWith(
    (async () => {
      // Respond from the cache if we can
      //const cachedResponse = await caches.match(event.request);
      //if (cachedResponse) return cachedResponse;

      // Else, use the preloaded response, if it's there
      //const response = await event.preloadResponse;
      //if (response) return response;

      // Else try the network.
      //console.log('fetch intercepted')
      return fetch(event.request);
    })(),
  );
});
*/

// weather
const w_icing = (val) => val && val.hum >= data.icingHum && val.wind <= data.icingWind && val.temp >= data.icingTemp[0] && val.temp <= data.icingTemp[1]
const w_wind = (val) => val && val.wind >= data.windWind
const w_cold = (val) => val && val.temp <= data.coldTemp
const w_hot = (val) => val && val.temp >= data.hotTemp

const weatherOptions = (color) => {
	return {
		color: color,
		weight: 1 / mapScale,
		opacity: 0.8,
		fillColor: color,
		fillOpacity: 0.2
	}
}
const weatherTooltipOffset = L.point(0, 12)
const weatherTooltopPosition = 'bottom'

const weatherMarker = (p, i) => {
	if (data.showIcingZones && w_icing(p.val[0]))
		if (w_icing(p.val[1]))
			return <CircleMarker key={i} center={[p.lat, p.lng]} radius={HI_METEO_RADIUS} pathOptions={weatherOptions('#0bb')} pane="weatherPoints" >
				<Tooltip className="weather-tooltip" sticky={true} offset={weatherTooltipOffset} direction={weatherTooltopPosition}>{`${p.name}. Длительное обледенение: темп ${p.val[0].temp} ветер ${p.val[0].wind} влажн ${p.val[0].hum}`}</Tooltip>
			</CircleMarker>
		else
			return <CircleMarker key={i} center={[p.lat, p.lng]} radius={LO_METEO_RADIUS} pathOptions={weatherOptions('#0cc')} pane="weatherPoints" >
				<Tooltip className="weather-tooltip" sticky={true} offset={weatherTooltipOffset} direction={weatherTooltopPosition}>{`${p.name}. Обледенение: темп ${p.val[0].temp} ветер ${p.val[0].wind} влажн ${p.val[0].hum}`}</Tooltip>
			</CircleMarker>
		
	if (data.showWindZones && w_wind(p.val[0]))
		if (w_wind(p.val[1]))
			return <CircleMarker key={i} center={[p.lat, p.lng]} radius={HI_METEO_RADIUS} pathOptions={weatherOptions('#888')} pane="weatherPoints" >
				<Tooltip className="weather-tooltip" sticky={true} offset={weatherTooltipOffset} direction={weatherTooltopPosition}>{`${p.name}. Устойчивый сильный ветер ${p.val[0].wind}`}</Tooltip>
			</CircleMarker>
		
		else
			return <CircleMarker key={i} center={[p.lat, p.lng]} radius={LO_METEO_RADIUS} pathOptions={weatherOptions('#999')} pane="weatherPoints" >
				<Tooltip className="weather-tooltip" sticky={true} offset={weatherTooltipOffset} direction={weatherTooltopPosition}>{`${p.name}. Сильный ветер ${p.val[0].wind}`}</Tooltip>
			</CircleMarker>
		
	if (data.showColdTempZones && w_cold(p.val[0]))
		if (w_cold(p.val[1]))
			return <CircleMarker key={i} center={[p.lat, p.lng]} radius={HI_METEO_RADIUS} pathOptions={weatherOptions('#04b')} pane="weatherPoints" >
				<Tooltip className="weather-tooltip" sticky={true} offset={weatherTooltipOffset} direction={weatherTooltopPosition}>{`${p.name}. Устойчиво низкая температура ${p.val[0].temp}`}</Tooltip>
			</CircleMarker>
		
		else
			return <CircleMarker key={i} center={[p.lat, p.lng]} radius={LO_METEO_RADIUS} pathOptions={weatherOptions('#04c')} pane="weatherPoints" >
				<Tooltip className="weather-tooltip" sticky={true} offset={weatherTooltipOffset} direction={weatherTooltopPosition}>{`${p.name}. Низкая температура ${p.val[0].temp}`}</Tooltip>
			</CircleMarker>
	
	if (data.showHotTempZones && w_hot(p.val[0]))
		if (w_hot(p.val[1]))
			return <CircleMarker key={i} center={[p.lat, p.lng]} radius={HI_METEO_RADIUS} pathOptions={weatherOptions('#90d')} pane="weatherPoints" >
				<Tooltip className="weather-tooltip" sticky={true} offset={weatherTooltipOffset} direction={weatherTooltopPosition}>{`${p.name}. Устойчиво высокая температура ${p.val[0].temp}`}</Tooltip>
			</CircleMarker>
		
		else
			return <CircleMarker key={i} center={[p.lat, p.lng]} radius={LO_METEO_RADIUS} pathOptions={weatherOptions('#a0f')} pane="weatherPoints" >
				<Tooltip className="weather-tooltip" sticky={true} offset={weatherTooltipOffset} direction={weatherTooltopPosition}>{`${p.name}. Высокая температура ${p.val[0].temp}`}</Tooltip>
			</CircleMarker>
	
	return
}

const [icingZones, setIcingZones] = useState([])
const [coldZones, setColdZones] = useState([])
const [hotZones, setHotZones] = useState([])
const [windZones, setWindZones] = useState([])

const createClusters = (points, dist) => {
	const _p = featureCollection(points)
	const _c = clustersDbscan(_p, dist, {minPoints: 2})
	const res = {}
	_c.features.forEach(f => {
		if (f.properties.dbscan === 'core') {
			if (!res[f.properties.cluster]) res[f.properties.cluster] = featureCollection([])
			res[f.properties.cluster].features.push(f)
		} else {
			if (!res[f.properties.dbscan]) res[f.properties.dbscan] = []
			res[f.properties.dbscan].push(f)
		}
	})
	return res
}

const cluster2leaflet = (v, size, others) => {
	const _con = convex(v)
	if (!_con) {
		//console.log(v)
		others && others.push(...v.features)
	}
	return _con ? simplify(buffer(_con.geometry, size), {tolerance: 0.01}).geometry.coordinates.map(ar => ar.map(e => e.reverse())) : []
}

const setupIcingZones = () => {
	if (data.showIcingZones) {
		const _p_icing = Object.values(data.weatherArchive).filter(p => w_icing(p.val[0])).map(p => point([p.lng, p.lat]))
		const _cl_icing = createClusters(_p_icing, 100)
		const _z_icing = Object.entries(_cl_icing).filter(([k, v]) => !['noise', 'edge'].includes(k) && v.features.length > 2).map(([k, v]) => cluster2leaflet(v, 30))
		setIcingZones(_z_icing)
	} else {
		setIcingZones([])
	}
}

const setupWindZones = () => {
	if (data.showWindZones) {
		const _p_wind = Object.values(data.weatherArchive).filter(p => w_wind(p.val[0])).map(p => point([p.lng, p.lat]))
		const _cl_wind = createClusters(_p_wind, 100)
		const _z_wind = Object.entries(_cl_wind).filter(([k, v]) => !['noise', 'edge'].includes(k) && v.features.length > 2).map(([k, v]) => cluster2leaflet(v, 50))
		setWindZones(_z_wind)
	} else {
		setWindZones([])
	}
}

const setupColdTempZones = () => {
	if (data.showColdTempZones) {
		const _p_cold = Object.values(data.weatherArchive).filter(p => w_cold(p.val[0])).map(p => point([p.lng, p.lat]))
		const _cl_cold = createClusters(_p_cold, 300)
		const _z_cold = Object.entries(_cl_cold).filter(([k, v]) => !['noise', 'edge'].includes(k) && v.features.length > 2).map(([k, v]) => cluster2leaflet(v, 50))
		setColdZones(_z_cold)
	} else {
		setColdZones([])
	}
}

const setupHotTempZones = () => {
	if (data.showHotTempZones) {
		const _p_hot = Object.values(data.weatherArchive).filter(p => w_hot(p.val[0])).map(p => point([p.lng, p.lat]))
		const _cl_hot = createClusters(_p_hot, 100)
		const _z_hot = Object.entries(_cl_hot).filter(([k, v]) => !['noise', 'edge'].includes(k) && v.features.length > 2).map(([k, v]) => cluster2leaflet(v, 50))
		setHotZones(_z_hot)
	} else {
		setHotZones([])
	}
}

useEffect(() => {
	if (data.showWeatherArchive) setupIcingZones()
}, [data.weatherArchive, data.showWeatherArchive, data.icingHum, data.icingWind, data.icingTemp, data.showIcingZones])

useEffect(() => {
	if (data.showWeatherArchive) setupWindZones()
}, [data.weatherArchive, data.showWeatherArchive, data.windWind, data.showWindZones])

useEffect(() => {
	if (data.showWeatherArchive) setupColdTempZones()
}, [data.weatherArchive, data.showWeatherArchive, data.coldTemp, data.showColdTempZones])

useEffect(() => {
	if (data.showWeatherArchive) setupHotTempZones()
}, [data.weatherArchive, data.showWeatherArchive, data.hotTemp, data.showHotTempZones])




const findMarker = (id) => {
	const markerImg = document.querySelector(`img[alt="${id}"]`)
	if (markerImg) return markerImg
	const markerDiv = document.querySelector(`div[alt*="${id}"]`)
	return markerDiv ? markerDiv : null
}
const getMarkers = (ids) => {
	const arr = document.querySelector('.leaflet-map-pane').getAttribute('style').split('translate3d(')[1].split(');')[0].split(',').map(e => Number(e.replace('px', '')))
	const root = document.querySelector('#map-container').getBoundingClientRect()
	return [...new Set(ids.map(id => findMarker(id)))]
		.filter(e => e)
		.map(e => {return {p: e.getBoundingClientRect(), ids: e.attributes.alt.value.split(' ')}})
		.map(e => {return {
			p: L.point(
				(e.p.x + e.p.width/2)/mapScale - arr[0] - root.left / mapScale, 
				(e.p.y + e.p.height/2)/mapScale - arr[1] - root.top / mapScale + (ENV.scrollOffset ? window.scrollY * mapScale : 0)
			),
			ids: e.ids}}
		)
}

const dialogLinksOptions = (highlighted) => {
	return {
		color: 'rgb(38, 143, 255)',
		weight: (highlighted ? 2 : 1) / mapScale,
		opacity: highlighted ? 1 : 0.8,
	}
}

const lineOptions = (width, highlighted = false, dashed = false) => {
	return {
		color: highlighted ? 'rgb(255, 38, 38)' : 'rgb(38, 143, 255)',
		weight: width * (data.useCustomViewport ? data.customLineScale : 1),
		opacity: 1,
		fillColor: highlighted ? 'rgb(255, 38, 38)' : 'rgb(38, 143, 255)',
		fillOpacity: 0.3,
		dashArray: dashed ? '4, 6' : undefined
	}
}

const massOptions = {
	color: 'rgb(255, 38, 38)',
	weight: 0,
	opacity: 0.5,
	//fillColor: 'rgb(255, 38, 38)',
	fillColor: 'url(#mass-mesh)',
	fillOpacity: 1,
}

const regionBorderOptions = {
	color: '#777',
	weight: 2 * (data.useCustomViewport ? data.customLineScale : 1),
	opacity: 1,
	fillOpacity: 0
}

const fireRadius = (m) => ((6 - m.d / 3) + (zoom - mapType.MIN_ZOOM)) * (data.useCustomViewport ? data.customMarkerScale : 1)
const fire_colors =       ['#d00', '#f00', '#f11', '#f22', '#f33', '#f44', '#f55', '#f66', '#f77', '#f88', '#faa', '#fcc']
const fire_line_opacity = [ 0.9  ,  0.8  ,  0.7  ,  0.6  ,  0.5  ,  0.45 ,  0.4  ,  0.35 ,  0.3  ,  0.25 ,  0.25 ,  0.25 ]

const fireOptions = (m) => {
	return {
		color: fire_colors[m.d],
		weight: 1 * (data.useCustomViewport ? data.customLineScale : 1),
		opacity: fire_line_opacity[m.d],
		//weight: 0,
		fillColor: fire_colors[m.d],
		fillOpacity: fire_line_opacity[m.d] / 3,
	}
}

const specobjectOptions = () => {
	return {
		color: 'rgb(0,128,255)',
		weight: 1,
		opacity: 0.9,
		fillColor: 'rgb(0,128,255)',
		fillOpacity: 0.25
	}
}

const specareaOptions = () => {
	return {
		color: 'rgb(255,0,128)',
		weight: 2.5,
		opacity: 0.9,
		fillColor: 'rgb(255,0,128)',
		fillOpacity: 0.2
	}
}

const weatherRegOptions = (e) => {
	return {
		color: '#A12222',
		weight: 2,
		opacity: data.showWeatherDangers && e.p ? 1 : 0,
		fillColor: '#A12222',
		fillOpacity: data.showWeatherWarnings && e.w === 'break' ? 0.25 : 0
	}
}

const milobjectsOptions = (e, zoom) => {
	const red = Math.max(40, 255 - Math.max(0, e.ka_event_date.length - 30)*5)
	return {
		color: `rgb(${red},0,0)`,
		weight: 1,
		opacity: 0.7,
		fillColor: `rgb(${red},0,0)`,
		fillOpacity: Math.min(1 - Math.max(0, zoom - 10) / 10, 0.1 + (e.ka_event_date.length / 30)) 
	}
}

const milRadius = (e, zoom) => zoom + e.ka_event_date.length / 50



/*
<defs>
	<pattern id="diag-1" width="6" height="6" patternTransform="rotate(-15 0 0)" patternUnits="userSpaceOnUse">
		<line x1="0" y1="0" x2="0" y2="6" style="stroke:#fc2300; stroke-width:1.8; stroke-opacity:1"></line>
	</pattern>
	<pattern id="diag-2" width="6" height="6" patternTransform="rotate(-75 0 0)" patternUnits="userSpaceOnUse">
		<line x1="3" y1="0" x2="3" y2="6" style="stroke:#009fff; stroke-width:1.5; stroke-opacity:1"></line>
	</pattern>
	<pattern id="diag-3" width="6" height="6" patternTransform="rotate(-135 0 0)" patternUnits="userSpaceOnUse">
		<line x1="0" y1="0" x2="0" y2="6" style="stroke:#000; stroke-width:2; stroke-opacity:0.6"></line>
	</pattern>
</defs>
*/

/*
 * ---
 */	
	
	
	const noObject = (center, id) => <CircleMarker key={id} className="no-events" center={center} radius={10} pathOptions={lineOptions(1.5, false)} pane={'lines'}>
		<Tooltip key={id} className="no-object" direction="center" permanent={true}>?</Tooltip>
	</CircleMarker>
	
	const drawObjects = (event) => {
		if (!event) return
		const objs = event.ka_object_uuid.substring(1, event.ka_object_uuid.length-1).split(',').filter(u => u !== 'NULL')
			.map(e => data.objCache[e.toUpperCase()])
			.filter(e => e)
			.sort((a, b) => a.uuid === dialogs[0].objuuid ? 1 : b.uuid === dialogs[0].objuuid ? -1 : 0)
		return objs.length === 0
		? event._m !== 't' && noObject(findCenter(firstRegion(event.regionId)), event.uuid + 'm')
		: objs.map((e, i) => e.line
			? <Polyline key={e.uuid + i} positions={e.line} pathOptions={lineOptions(3, e.uuid === dialogs[0].objuuid)} pane={'lines'} eventHandlers={{'click': () => {
					props.onShowObject(e.uuid)
				}}}/>
			: e.latlng ? <CircleMarker key={e.uuid + i} center={e.latlng} radius={10} pathOptions={lineOptions(1.5, e.uuid === dialogs[0].objuuid)} pane={'lines'} eventHandlers={{'click': () => {
					props.onShowObject(e.uuid)
				}}}/>
			: !e.latlng && !dialogs[0].objuuid && !objs.some(o => o.line || o.latlng) && noObject(findCenter(firstRegion(event.regionId)), event.uuid + i)
			)
	}
	
	const markRegion = (event) => {
		if (!event || event._m !== 't') return
		return <Polygon positions={obj2regions({reg: lo_regions[firstRegion(event.regionId)]})} pathOptions={massOptions} pane="regions"/>
	}
	
	
	
	const resetMapView = () => {
		setZoom(mapType.ZOOM)
		setCenter(mapType.CENTER)
		_map.setView(mapType.CENTER, mapType.MIN_ZOOM)
		_map.scrollWheelZoom.disable()
	}
	
	useImperativeHandle(ref, () => ({
		getMap() {
			return _map
		},
		resetMap() {
			resetMapView()
		},
		centerTo(id) {
			const markers = getMarkers([id])
			if (!markers.length) return
			const marker = markers.map(e => e.p).reduce((a, e) => {return {x: a.x + e.x / markers.length, y: a.y + e.y / markers.length}}, {x: 0, y: 0})
			setCenter(_map.layerPointToLatLng(marker))
			_map.setView(_map.layerPointToLatLng(marker), zoom)
		}
	}))
	
  useEffect(() => {
		document.documentElement.style.setProperty('--map-height', height / mapScale)
		if (_map) {
			_map.invalidateSize(false)
		}
	}, [height])
	
	/*
	 * Map zoom & move event handlers
	 */
	
	const MapCompponent = () => {
		const map = useMapEvents({
			zoom: () => {
				if (map.getZoom() === mapType.MIN_ZOOM) {
					resetMapView()
				} else {
					setZoom(map.getZoom())
					map.scrollWheelZoom.enable()
					if (ENV.DEBUG) console.log('zoom:', map.getZoom(), 'mapScale:', mapScale)
				}
			},
			move: () => {
				//if (ENV.DEBUG) console.log(map.getCenter())
				setCenter(map.getCenter())
				//update()
			},
		})
		return 	null
  }
  
	useEffect(() => {
		_map?.getContainer().addEventListener('wheel', e => {
			//console.log(e.deltaY, _map.getZoom(), mapType.MIN_ZOOM)
			if (_map.getZoom() === mapType.MIN_ZOOM && e.deltaY < 0 && window.scrollY === 0) {
				//console.log('zoom up', e, _map)
				_map.scrollWheelZoom.enable()
				const arr = document.querySelector('.leaflet-map-pane').getAttribute('style').split('translate3d(')[1].split(');')[0].split(',').map(e => Number(e.replace('px', '')))
				const root = document.querySelector('#map-container').getBoundingClientRect()
				const client = _map.layerPointToLatLng(L.point(
					e.clientX / mapScale - arr[0] - root.left * mapScale, e.clientY / mapScale - arr[1] - root.top * mapScale
				))
				const clientCntr = {lat: (client.lat + _map.getCenter().lat) / 2, lng: (client.lng + _map.getCenter().lng) / 2}
				_map.setView(clientCntr, mapType.MIN_ZOOM + 1)
			}
		})
	}, [_map])
	
	return <>
		<svg className="height-0">
			<defs>
				<pattern id="diag-1" width="6" height="6" patternTransform={`rotate(-15 0 0) scale(${1/mapScale} ${1/mapScale})`} patternUnits="userSpaceOnUse">
					<line x1="0" y1="0" x2="0" y2="6" style={{stroke:'#fc2300', strokeWidth:2, strokeOpacity:1}}></line>
				</pattern>
				<pattern id="img-pattern" width="160" height="160" patternUnits="userSpaceOnUse">
					<image width="160" height="160" xlinkHref="/info-map/img/info-map/abstract_maze_design_pattern_background_2303.jpg" />
				</pattern>
				<pattern id="img-among" width="108" height="114" patternUnits="userSpaceOnUse">
					<image width="108" height="114" xlinkHref="/info-map/img/info-map/amongus.gif" />
				</pattern>
				<pattern id="mass-mesh" width="256" height="256" patternUnits="userSpaceOnUse">
					<image width="256" height="256" xlinkHref={`${ENV.imgUrl}mass2.svg`} />
				</pattern>
			</defs>
		</svg>
		<style id="uuid-style">
			{dialogs.map(e => e.ids).reduce((a, e) => [...a, ...e], []).map(e => `img[alt="${e}"]`).join(',')}
			{dialogs.length > 0 && `{box-shadow: 0 0 5px 0.05rem rgb(38, 143, 255);}`}
			{false && Object.keys(data.svgFiles).map(k => `img[src="${k}"] {background-color: red; background-image: url("${data.svgFiles[k]}");}`)}
			
		</style>
		<MapContainer
			className={'map-leaflet ' + zoom}
			center={center}
			zoom={zoom}
			minZoom={mapType.MIN_ZOOM}
			maxZoom={mapType.MAX_ZOOM}
			ref={_setMap}
			zoomControl={false}
			scrollWheelZoom={false}
			attributionControl={false}
		>
			<MapCompponent />
			{mapType.TILES_URL && <TileLayer url={mapType.TILES_URL} />}
			
			<Pane name="regions"/>
			<Pane name="weather"/>
			<Pane name="quake"/>
			<Pane name="weatherZones"/>
			<Pane name="weatherPoints"/>
			<Pane name="firePoints"/>
			<Pane name="events"/>
			<Pane name="deenergized"/>
			<Pane name="fireClusters"/>
			<Pane name="dialogLinks"/>
			<Pane name="lines"/>
			
			<Polygon positions={out_regions_shape} pathOptions={mapType.outRegionOptions(zoom)} pane="regions"/>
			
			{ data.regionBorders.length &&
				<Polygon positions={data.regionBorders.map(e => hi_regions.filter(hi => hi.regCode == e.code).map(hi => hi.polygon))} pathOptions={regionBorderOptions} pane="regions" />
			}
			
			{_specobjects && [51,52].map((e, i) => <Polygon key={i} positions={shape_6r[e]} pathOptions={specareaOptions()} pane="weather" />)}
			{_specobjects && <Polygon positions={Object.values(zk)} pathOptions={specareaOptions()} pane="weather" />}
			{_specobjects && <Polygon positions={Object.values(shape_6r)} pathOptions={specobjectOptions()} pane="quake" />}

			{decoration.showDialogLinks && dialogs.map((d, i) => {
				const ids = d.ids
				let dialog = document.querySelector(`div[uuid="${ids.length}-${ids[0]}"]`)?.getBoundingClientRect()
				if (!dialog) {
					const width = window.innerWidth
						|| document.documentElement.clientWidth
						|| document.body.clientWidth
					dialog = {
						left: width / 2,
						right: width / 2,
						top: ENV.defaultDialogTop
					}
				}
				const markerPoints = getMarkers(ids)
				
				const arr = document.querySelector('.leaflet-map-pane').getAttribute('style').split('translate3d(')[1].split(');')[0].split(',').map(e => Number(e.replace('px', '')))
				const root = document.querySelector('#map-container').getBoundingClientRect()
				return markerPoints.map((markerPoint, ix) => {
					return <Polyline key={`${i}-${ix}`} pathOptions={dialogLinksOptions(i === 0 && markerPoint.ids.includes(d.active))} pane="dialogLinks" 
						positions={[
							_map.layerPointToLatLng(markerPoint.p),
							_map.layerPointToLatLng(L.point(((dialog.left + dialog.right)/2)/mapScale - arr[0] - root.left / mapScale, (dialog.top + 18)/mapScale - arr[1] - root.top / mapScale + (ENV.scrollOffset ? window.scrollY * mapScale : 0) ))
						]} /> })
			})}

			{ dialogs.length && drawObjects(data.events.find(e => e.id === dialogs[0].active)) }
			{ dialogs.length && markRegion(data.events.find(e => e.id === dialogs[0].active)) }
			
			{ _specobjects
				? <MarkerClusterGroup
						showCoverageOnHover={false}
						zoomToBoundsOnClick={false}
						spiderfyOnMaxZoom={false}
						disableClusteringAtZoom={mapType.MAX_ZOOM + 1}
						iconCreateFunction={createSpecobjectCluster}
						removeOutsideVisibleBounds={false}
						maxClusterRadius={clasterRadius() / mapScale}
						onClick={evt => {
						}}
						onMouseOver={evt => {
							const tooltip = L.tooltip({className: 'marker-tooltip', sticky: true, offset: markerTopTooltipOffset, direction: 'top'}).setContent(`<p class="m-0">${evt.sourceTarget.getAllChildMarkers().map(e => e.options.name).sort().join('<br/>')}</p>`)
							evt.propagatedFrom.bindTooltip(tooltip).openTooltip()
						}}
						onMouseOut={evt => evt.propagatedFrom.bindTooltip()}
					>
						{Object.values(data.specobjects).map((o, i) => specobjectMarker(o, i))}
					</MarkerClusterGroup>
				: region_keys.map(r => {
					const reg_events = data.fEvents.filter(e => e.regionId == r).sort(eventCompare)
					if (reg_events.length) return	<MarkerClusterGroup key={r + moment().valueOf()}
							showCoverageOnHover={false}
							zoomToBoundsOnClick={false}
							spiderfyOnMaxZoom={false}
							disableClusteringAtZoom={mapType.MAX_ZOOM + 1}
							iconCreateFunction={createEventCluster}
							removeOutsideVisibleBounds={false}
							maxClusterRadius={clasterRadius() / mapScale}
							onClick={evt => {
								props.onShowEventDetails(evt.layer.getAllChildMarkers().map(e => e.options.uuid))
							}}
							onMouseOver={evt => {
								const regName = oktmo2name[data.events.find(e => e.id === evt.sourceTarget.getAllChildMarkers()[0].options.uuid).regionId]
								const tooltip = L.tooltip({className: 'marker-tooltip', sticky: true, offset: markerTopTooltipOffset, direction: 'top'}).setContent(`<p class="m-0">${regName}</p>`)
								evt.propagatedFrom.bindTooltip(tooltip).openTooltip()
							}}
							onMouseOut={evt => evt.propagatedFrom.bindTooltip()}
						>	
							{reg_events.map((e, i) => eventMarker(e, i))}
						</MarkerClusterGroup>
				})
			}
			
			{ _specobjects && data.milobjects.filter(e => e.lat && e.lng).map((e, i) => 
				<CircleMarker key={i} center={[e.lat, e.lng]} radius={milRadius(e, zoom)} pathOptions={milobjectsOptions(e, zoom)} pane="firePoints">
					<Tooltip className="marker-tooltip" sticky={true} offset={markerTopTooltipOffset} direction={'top'}>
						{/*<><p className="m-0 mb-2 font-bold">{event.startincident}{event.de ? ' – ' + event.resolvedata : ''}</p>{event.title.map((e, i) => <p key={i} className="m-0">{e}</p>)}</>*/}
						<>
							<p className="m-0 mb-2 font-bold">{e.name}</p>
							{e.ka_event_date.length > 10 
								? <>
										<p className="m-0 font-bold">всего событий: {e.ka_event_date.length}</p>
										{e.ka_event_date.filter((el, i) => i < 8).map((el, i) => <p key={i} className="m-0">{el.format('DD-MM-YYYY HH:mm')}</p>)}
										<p className="m-0 mb-1">...</p>
										<p className="m-0">{e.ka_event_date[e.ka_event_date.length-1].format('DD-MM-YYYY HH:mm')}</p>
									</>
								: e.ka_event_date.map((el, i) => <p key={i} className="m-0">{el.format('DD-MM-YYYY HH:mm')}</p>)
							}
						</>
						
					</Tooltip>
				</CircleMarker>
			)}
			
			{ !_specobjects && data.showFire && data.fires.map((e, i) => 
				<CircleMarker key={i} center={[e.lat, e.lng]} radius={fireRadius(data.showFireActuality ? e : {d: 2})} pathOptions={fireOptions(data.showFireActuality ? e : {d: 2})} pane="firePoints" />
			)}
			
			{ false && !_specobjects && data.showFire && 
				region_keys.map(r => {
					const reg_fires = data.fires.filter(e => e.r == r)
					if (reg_fires.length) {
						const c = reg_fires.reduce((a, e) => {a.lat += e.lat; a.lng += e.lng; return a}, {lat: 0, lng: 0})
						return <CircleMarker key={r} center={[c.lat / reg_fires.length, c.lng / reg_fires.length]} radius={10} pane="fireClusters" />
					}
				})
			}
			
			{ !_specobjects && data.showFireRegCl && Object.keys(data.fireRegCl).map((e, i) => 
				fireRegMarker(e, data.fireRegCl[e], i)
			)}
			
			{ !_specobjects && data.showQuakes && data.fQuakes.map((e, i) =>
				earthquakeIcon(i, e, zoom)
			)}
			
			{ data.geoObjects.map(e => data.objCache[e]).filter(e => e).map((e, i) => 
				e.line ? <Polyline key={e.uuid + i} positions={e.line} pathOptions={lineOptions(3, true)} pane={'lines'} />
				
				: e.type === 3 && e.lats && e.late && e.lons && e.lone ? (() => {
						//console.log(e)
						if (e.lons > 10 && e.lone > 10)
						return <Polyline key={e.uuid + i} positions={[[e.lats, e.lons], [e.late, e.lone]]} pathOptions={lineOptions(3, true, true)} pane={'lines'} />
					})()
				
				: e.type === 3 ? null
				: e.latlng && <CircleMarker key={e.uuid + i} center={e.latlng} radius={10 * (data.useCustomViewport ? data.customMarkerScale : 1)} pathOptions={lineOptions(1.5, true)} pane={'lines'} />
			)}
			
			
			{ false && reg_centers.map((e, i) => 
				<CircleMarker key={i} center={[e.lat, e.lon]} radius={fireRadius({d: 2})} pathOptions={fireOptions({d: 2})} pane="lines">
					<Tooltip className="marker-tooltip" sticky={true} offset={markerTopTooltipOffset} direction={'top'}>
						<><p className="m-0">{e.id}</p></>
					</Tooltip>
				</CircleMarker>
			)}
			
			{ !_specobjects && data.showDeenergized && data.deenergized.map((e, i) => deenergizedMarker(e, i))}
			
			{ !_specobjects && (data.showWeatherWarnings || data.showWeatherDangers) && 
					data.weather.filter(([k, v]) => data.weatherFilterOptions.length === 0 
						|| data.showWeatherWarnings && v.wd?.title.some(e => data.weatherFilterOptions.map(o => o.n).includes(data.weather_warn_names[e]))
						|| data.showWeatherDangers && v.inf?.some(e => e.d.map(e => e.t).some(e => data.weatherFilterOptions.map(o => o.n).includes(e)))
					).map(([k, v], i) => 
							<Polygon key={i} positions={hi_regions.filter(hi => hi.regCode == k).map(hi => hi.polygon)} pathOptions={weatherRegOptions(v)} pane="weather" eventHandlers={{
									click: () => props.onShowWeatherReg(k)
								}}>
							</Polygon>
			)}
			
			{ !_specobjects && data.showWeatherArchive && data.showMeteoDetails && Object.entries(data.weatherArchive).map(([k, v], i) => weatherMarker(v, i)) }
			
			{ !_specobjects && data.showWeatherArchive && <Polygon positions={icingZones} pathOptions={weatherOptions('#0bb')} fillRule="nonzero" pane={'weatherZones'} /> }
			{ !_specobjects && data.showWeatherArchive && <Polygon positions={coldZones} pathOptions={weatherOptions('#04b')} fillRule="nonzero" pane={'weatherZones'} /> }
			{ !_specobjects && data.showWeatherArchive && <Polygon positions={hotZones} pathOptions={weatherOptions('#90d')} fillRule="nonzero" pane={'weatherZones'} /> }
			{ !_specobjects && data.showWeatherArchive && <Polygon positions={windZones} pathOptions={weatherOptions('#888')} fillRule="nonzero" pane={'weatherZones'} /> }
			
		</MapContainer>
	</>
})

export default Map

